/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd, buildBreadcrumbSchema, buildWebPageSchema } from '../../../lib/schemaBuilder';
import { ServiceAreasHero } from '../components/ServiceAreasHero';
import { WhyChooseUs } from '../components/WhyChooseUs';
import { NearbyAreasSection } from '../components/NearbyAreasSection';
import { ReadyToShopCta } from '../components/ReadyToShopCta';
import { Link } from 'react-router-dom';

export default function TillamookServiceAreaPage() {
  useSEOHead({
    title: 'Used Cars Near Tillamook OR | No Dealer Fees, Free Delivery | Rockhound Auto',
    description: 'Shop quality used cars near Tillamook, Oregon with free delivery. No dealer fees, coastal-ready vehicles, transparent pricing. Serving all of Tillamook County — no dealership pressure.',
    keywords: 'used cars near Tillamook Oregon, Tillamook used car dealer, no dealer fee cars Tillamook OR, used vehicles Tillamook County, free delivery used cars Tillamook',
    canonicalUrl: 'https://rockhoundauto.com/service-areas/tillamook',
    geoRegion: 'US-OR',
    geoPlacename: 'Tillamook, Oregon',
    geoPosition: '45.4557;-123.8451',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Service Areas',
          item: 'https://rockhoundauto.com/service-areas',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Tillamook',
          item: 'https://rockhoundauto.com/service-areas/tillamook',
        },
      ],
    };

    const webPageSchema = buildWebPageSchema({
      name: 'Used Cars in Tillamook Oregon | Rockhound Auto',
      description: 'Buy quality used cars in Tillamook, Oregon with free delivery. No dealership pressure, transparent pricing, and honest service.',
      url: 'https://rockhoundauto.com/service-areas/tillamook'
    });

    injectJsonLd('schema-tillamook-breadcrumb', breadcrumbSchema);
    injectJsonLd('schema-tillamook-webpage', webPageSchema);

    return () => {
      removeJsonLd('schema-tillamook-breadcrumb');
      removeJsonLd('schema-tillamook-webpage');
    };
  }, []);

  const nearbyAreas = [
    { name: 'Cannon Beach', slug: 'cannon-beach' },
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Warrenton', slug: 'warrenton' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <ServiceAreasHero
        city="Tillamook"
        state="Oregon"
        tagline="Buy a Car Without the Dealership Circus"
        description="We bring quality used cars to Tillamook with free delivery. No pressure, no games—just honest service and transparent pricing. Because buying a car shouldn't feel like a three-ring act."
        heroImage="https://public.readdy.ai/ai/img_res/b13569ce3dd996aadf5a3d2ab432aa38.jpg"
      />

      <WhyChooseUs city="Tillamook" />

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Tillamook Drivers Choose Rockhound Auto
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-truck-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Free Delivery to Tillamook</h3>
              <p className="text-gray-700">
                We'll bring your car right to your door—whether you're in downtown Tillamook or out by the cheese factory. No need to drive to a dealership.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Coastal-Ready Vehicles</h3>
              <p className="text-gray-700">
                Every vehicle is inspected for rust and coastal wear. We know what it takes to survive Oregon Coast weather—and we only sell cars that can handle it.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hand-heart-line text-2xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Dealership Drama</h3>
              <p className="text-gray-700">
                No high-pressure sales tactics. No hidden fees. Just transparent pricing and honest answers. We're here to help, not hustle.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Serving All of Tillamook County
          </h2>
          <div className="prose prose-lg max-w-4xl mx-auto text-gray-700">
            <p className="mb-4">
              From downtown Tillamook to Bay City, Garibaldi, and Rockaway Beach—we deliver quality used cars throughout Tillamook County. Whether you're commuting to work, hauling gear to the coast, or just need a reliable ride for daily errands, we've got you covered. For a full breakdown of available inventory and buyer resources, visit our <Link to="/used-cars-tillamook-oregon" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">used cars Tillamook Oregon</Link> page.
            </p>
            <p className="mb-4">
              We know Tillamook drivers need vehicles that can handle wet roads, salty air, and the occasional trip over the Coast Range. That's why every car we sell is thoroughly inspected and ready for Oregon Coast life.
            </p>
            <p>
              Plus, we support local causes—including Oregon Dog Rescue. Every car we sell helps fund their mission to save dogs in need. So you're not just buying a car; you're helping pups find their forever homes.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Full Tillamook Buyer Guide</h4>
                <p className="text-slate-600 text-sm">
                  See our dedicated Tillamook page — work trucks for the dairy loop, coastal-ready vehicles, free delivery details, and why Tillamook County buyers skip the Portland dealerships.
                </p>
              </div>
              <Link
                to="/used-cars-tillamook-oregon"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm"
              >
                Used Cars Tillamook OR <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Tillamook" nearbyAreas={nearbyAreas} />

      <ReadyToShopCta cityName="Tillamook" />
    </div>
  );
}

export { TillamookServiceAreaPage };
